import React, { useState } from 'react';
import { Box, Button, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { WarningAmber as WarningAmberIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilling } from '../../context/BillingContext';
import { BILLING_SETTINGS_PATH, changePlanPath } from './billingPaths';
import { choosePlanButtonSx, daysLeftTitle, reachedTimeWarning, softToneColor } from './freeAccessNotice';
import { isRenewalInGrace } from './renewalNotice';

/**
 * Aviso de que te estás acercando al límite: por uso (los umbrales los da el backend: el de
 * siempre en un plan de pago; 50 % y 80 % en una prueba o un comp) y, en una prueba o un comp,
 * por los días que le quedan (#759 D18: el comp a 7 y 1 día, la prueba a 3 y 1).
 *
 * NO CUBRE EL PLAN AGOTADO, Y ESO ES UNA DECISIÓN (#1300)
 * --------------------------------------------------------
 * El plan agotado NO tiene tarjeta flotante. Se probaron los dos sitios obvios y
 * los dos estorbaban: a ancho completo pegada al techo tapaba la parte alta del
 * menú lateral, y anclada abajo a la derecha tapaba el botón de parar del
 * compositor. Un aviso que no se puede cerrar y que además estorba sale más caro
 * que el problema que resuelve.
 *
 * El estado «sin crédito» se comunica por otras tres vías, y ninguna ocupa sitio
 * en la pantalla de trabajo:
 *   1. el triángulo permanente sobre el avatar (`UserMenu`),
 *   2. la entrada en rojo de ese menú, que es la que lleva a pedir más uso,
 *   3. el aviso de Configuración → Facturación.
 * Y el modal sigue siendo quien interrumpe en el momento del corte.
 *
 * Éste sí se cierra: es informativo y todavía puedes trabajar. Cada aviso se cierra una vez:
 * el siguiente umbral, de uso o de días, vuelve a abrirlo. El descarte no se guarda, así que al
 * volver a entrar sale otra vez: es el recordatorio de cada login mientras le quede tiempo. Cerrado,
 * una prueba o un comp siguen llegando a los planes desde el menú del avatar y desde
 * Configuración → Facturación.
 *
 * El aviso por días va en rojo: si no elige plan, se queda sin acceso, como con el plan agotado.
 * El de uso sigue en naranja: todavía puede trabajar. Los dos suavizados (`softToneColor`), y en una
 * prueba o un comp el botón «Elegir un plan» va en ese rojo suave, como en el menú del avatar.
 */

const UsageWarningBanner = () => {
  const { warningLevel, dismissWarning, usage, isExhausted } = useBilling();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const [dismissedTimeWarning, setDismissedTimeWarning] = useState(null);
  // Agotado, manda el modal central: este aviso se aparta también con los días de una prueba o un
  // comp, que si no se sumaban al modal.
  if (isExhausted) return null;
  // #759 R: una renovación sin cobrar tiene su propio aviso (`RenewalGraceNotice`), en este mismo
  // sitio y más importante que el consumo.
  if (isRenewalInGrace(usage)) return null;

  const freeAccess = usage?.freeAccess ?? null;
  const timeWarning = reachedTimeWarning(freeAccess);
  const showTime = timeWarning !== null && timeWarning !== dismissedTimeWarning;
  const showUsage = warningLevel !== null && warningLevel < 100;
  if (!showTime && !showUsage) return null;

  const pct = Math.round(Number(usage?.percentUsed ?? warningLevel));
  const usageTitle = freeAccess
    ? t('billing.warning.usageTrial', { pct, defaultValue: 'Estás al {{pct}}% del límite de tu prueba' })
    : t('billing.warning.usagePlan', { pct, defaultValue: 'Estás al {{pct}}% del límite de tu plan' });
  // #1383: /billing/required abre Checkout, que crea una suscripción NUEVA. A
  // quien ya paga por Stripe eso le cobraría dos veces: su sitio es Facturación.
  // #759 FE-C4: con el plan siguiente a mano (y quien puede pagarlo), el botón abre su diálogo.
  const upgradeTo = usage?.upgrade?.kind === 'change' && usage?.canManageBilling ? usage.upgrade : null;
  const plansPath = upgradeTo
    ? changePlanPath(upgradeTo.planId)
    : (usage?.source === 'stripe' ? BILLING_SETTINGS_PATH : '/billing/required');
  const markColor = softToneColor(theme, showTime ? 'error' : 'warning');

  const close = () => {
    if (showTime) setDismissedTimeWarning(timeWarning);
    if (showUsage) dismissWarning();
  };

  return (
    <Paper
      elevation={6}
      data-testid="usage-banner"
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9998,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        p: 2,
        borderRadius: 2,
        borderLeft: 4,
        borderColor: markColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <WarningAmberIcon sx={{ color: markColor, mt: '2px' }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {showTime ? daysLeftTitle(t, freeAccess.daysLeft) : usageTitle}
          </Typography>
          {showTime && showUsage && (
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
              {usageTitle}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {freeAccess
              ? t('billing.warning.bodyTrial', 'Cuando termine, tendrás que elegir un plan para seguir usando AIFlock. Tus agentes y tus conversaciones se quedan.')
              : t('billing.warning.bodyPlan', 'Cuando llegues al 100% no podrás enviar mensajes hasta que se renueve tu período o actualices tu plan.')}
          </Typography>

          <Button
            color="warning"
            variant="contained"
            size="small"
            disableElevation
            onClick={() => navigate(plansPath)}
            sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600, ...(freeAccess && choosePlanButtonSx(theme)) }}
          >
            {freeAccess
              ? t('billing.freeAccess.choosePlan', 'Elegir un plan')
              : upgradeTo
                ? t('billing.limit.upgradeTo', { plan: upgradeTo.planName, defaultValue: 'Subir a {{plan}}' })
                : t('billing.warning.seePlans', 'Ver planes')}
          </Button>
        </Box>

        <Tooltip title={t('billing.warning.close', 'Cerrar')}>
          <IconButton size="small" aria-label={t('billing.warning.closeAria', 'Cerrar aviso')} onClick={close}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default UsageWarningBanner;
